
import { supabase } from '@/integrations/supabase/client';
import { Transaction, Category, CreditCard, BankAccount, Budget, Goal } from '@/types';
import { getCurrentActiveProfileId } from './supabase-profiles-operations';
import { encryptField, decryptField } from './encryption-service';

// Helper function to get current profile ID
export const getCurrentProfileId = (): string | null => {
  const profileId = getCurrentActiveProfileId();
  console.log('[Profile] Current active profile ID:', profileId);
  return profileId;
};

// Helper function to add profile_id to data
export const addProfileId = <T extends Record<string, any>>(data: T): T & { profile_id: string | null } => {
  return {
    ...data,
    profile_id: getCurrentProfileId()
  };
};

// TRANSACTIONS
// Helper function to map frontend status to database status
const mapStatusToDb = (status?: string): string => {
  switch (status) {
    case 'paid': return 'efetivada';
    case 'pending': return 'pendente';
    case 'partial': return 'efetivada'; // treat partial as efetivada for now
    default: return 'efetivada';
  }
};

// Helper function to map database status to frontend status
const mapStatusFromDb = (dbStatus?: string): 'paid' | 'pending' | 'partial' => {
  switch (dbStatus) {
    case 'pendente': return 'pending';
    case 'efetivada': return 'paid';
    case 'cancelada': return 'pending'; // treat cancelada as pending for now
    default: return 'paid';
  }
};

export const saveTransactionToSupabase = async (transaction: Transaction): Promise<void> => {
  console.log('[Supabase] Saving transaction with profile:', getCurrentProfileId());
  
  // Helper function to normalize UUID values for database
  const normalizeUUIDForDB = (value: any): string | null => {
    if (!value || value === '' || value === 'undefined') return null;
    return value;
  };
  
  try {
    // Encrypt sensitive fields
    const descriptionEncrypted = await encryptField(transaction.description);
    const amountEncrypted = await encryptField(String(transaction.amount));
    
    const transactionData = addProfileId({
      id: transaction.id,
      description: '[ENCRYPTED]',
      description_encrypted: descriptionEncrypted.encrypted,
      description_nonce: descriptionEncrypted.nonce,
      amount: 0,
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      category_id: normalizeUUIDForDB(transaction.categoryId),
      date: transaction.date,
      type: transaction.type,
      account_id: normalizeUUIDForDB(transaction.accountId),
      payment_method: transaction.paymentMethod,
      card_id: normalizeUUIDForDB(transaction.cardId),
      status: mapStatusToDb(transaction.status),
      installments: transaction.installments || 1,
      installment_number: transaction.installmentNumber || 1,
      parent_transaction_id: normalizeUUIDForDB(transaction.parentTransactionId),
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    console.log('[Supabase] Normalized transaction data for DB (encrypted)');

    const { error } = await supabase
      .from('transactions')
      .upsert(transactionData);

    if (error) {
      console.error('[Supabase] Error saving transaction:', error);
      throw new Error(`Erro ao salvar transação: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da transação');
  }
};

export const loadTransactionsFromSupabase = async (): Promise<Transaction[]> => {
  const profileId = getCurrentProfileId();
  console.log('[Supabase] Loading transactions for profile:', profileId);
  console.log('[Debug] Profile isolation check - Active profile ID:', profileId);
  
  let query = supabase
    .from('transactions')
    .select('*')
    .order('date', { ascending: false });

  // Filter by profile if one is active
  if (profileId) {
    console.log('[Debug] Adding profile filter:', profileId);
    query = query.eq('profile_id', profileId);
  } else {
    console.log('[Debug] No active profile, returning empty result');
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading transactions:', error);
    throw new Error(`Erro ao carregar transações: ${error.message}`);
  }

  const results: Transaction[] = [];
  const failedTransactions: string[] = [];

  // Process each transaction individually to handle decryption failures gracefully
  for (const row of data || []) {
    try {
      // Decrypt sensitive fields
      const description = await decryptField({
        encrypted: row.description_encrypted,
        nonce: row.description_nonce
      });
      
      const amount = await decryptField({
        encrypted: row.amount_encrypted,
        nonce: row.amount_nonce
      });
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
        if (decrypted) return decrypted;
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        description: cleanValue(description, row.description),
        amount: amount ? Number(amount) : Number(row.amount || 0),
        category: '', // Will be populated by context
        categoryId: row.category_id || '',
        date: row.date,
        type: row.type as 'receita' | 'despesa',
        account: '', // Will be populated by context
        accountId: row.account_id,
        paymentMethod: row.payment_method as any,
        cardId: row.card_id,
        status: mapStatusFromDb(row.status),
        installmentNumber: row.installment_number,
        installments: row.installments,
        parentTransactionId: row.parent_transaction_id,
        createdAt: row.created_at
      });
    } catch (decryptError) {
      // Log failed transaction but add with fallback values
      console.warn(`[Supabase] ⚠️ Failed to decrypt transaction ${row.id}, using fallback values`);
      failedTransactions.push(row.id);
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (fallback: string | null | undefined): string => {
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      // Add transaction with fallback values
      results.push({
        id: row.id,
        description: cleanValue(row.description) || '⚠️ Erro ao descriptografar',
        amount: Number(row.amount || 0),
        category: '',
        categoryId: row.category_id || '',
        date: row.date,
        type: row.type as 'receita' | 'despesa',
        account: '',
        accountId: row.account_id,
        paymentMethod: row.payment_method as any,
        cardId: row.card_id,
        status: mapStatusFromDb(row.status),
        installmentNumber: row.installment_number,
        installments: row.installments,
        parentTransactionId: row.parent_transaction_id,
        createdAt: row.created_at,
        hasDecryptError: true
      });
    }
  }

  // Log failed transactions but don't throw - return partial results
  if (failedTransactions.length > 0) {
    console.warn(`[Supabase] ⚠️ ${failedTransactions.length} de ${data?.length || 0} transações com erro de descriptografia`);
    console.warn('[Supabase] 💡 Dados legados ou chave desatualizada detectada');
    console.warn('[Supabase] 📋 IDs das transações com falha:', failedTransactions);
    console.warn('[Supabase] ✅ Retornando transações descriptografadas com sucesso:', results.length);
  }

  return results;
};

export const deleteTransactionFromSupabase = async (transactionId: string): Promise<void> => {
  console.log('[Supabase] Deleting transaction:', transactionId);

  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('id', transactionId);

  if (error) {
    console.error('[Supabase] Error deleting transaction:', error);
    throw new Error(`Erro ao excluir transação: ${error.message}`);
  }
};

// CATEGORIES
export const saveCategoryToSupabase = async (category: Category): Promise<void> => {
  console.log('[Supabase] Saving category with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(category.name);
    
    const categoryData = addProfileId({
      id: category.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      type: category.type,
      color: category.color,
      icon: category.icon,
      is_default: category.isDefault || false,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('categories')
      .upsert(categoryData);

    if (error) {
      console.error('[Supabase] Error saving category:', error);
      throw new Error(`Erro ao salvar categoria: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da categoria');
  }
};

export const loadCategoriesFromSupabase = async (): Promise<Category[]> => {
  console.log('[Supabase] Loading categories for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('categories')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, filter by NULL profile_id
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading categories:', error);
    throw new Error(`Erro ao carregar categorias: ${error.message}`);
  }

  const results: Category[] = [];
  
  for (const row of data || []) {
    try {
      // Decrypt sensitive fields
      const name = await decryptField({
        encrypted: row.name_encrypted,
        nonce: row.name_nonce
      });
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
        if (decrypted) return decrypted;
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(name, row.name),
        type: row.type as 'receita' | 'despesa',
        color: row.color,
        icon: row.icon,
        isDefault: row.is_default
      });
    } catch (decryptError) {
      console.warn(`[Supabase] ⚠️ Failed to decrypt category ${row.id}, using fallback values`);
      
      const cleanValue = (fallback: string | null | undefined): string => {
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(row.name) || 'Categoria',
        type: row.type as 'receita' | 'despesa',
        color: row.color,
        icon: row.icon,
        isDefault: row.is_default
      });
    }
  }
  
  return results;
};

export const deleteCategoryFromSupabase = async (categoryId: string): Promise<void> => {
  console.log('[Supabase] Deleting category:', categoryId);

  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', categoryId);

  if (error) {
    console.error('[Supabase] Error deleting category:', error);
    throw new Error(`Erro ao excluir categoria: ${error.message}`);
  }
};

// CARDS
export const saveCardToSupabase = async (card: CreditCard): Promise<void> => {
  console.log('[Supabase] Saving card with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(card.name);
    const bankEncrypted = await encryptField(card.bank || '');
    const limitEncrypted = await encryptField(String(card.limit));
    const usedEncrypted = await encryptField(String(card.used));
    
    const cardData = addProfileId({
      id: card.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      bank: '[ENCRYPTED]',
      bank_encrypted: bankEncrypted.encrypted,
      bank_nonce: bankEncrypted.nonce,
      type: 'credito', // Fixed: Use 'credito' to match database constraint
      limit: 0,
      limit_encrypted: limitEncrypted.encrypted,
      limit_nonce: limitEncrypted.nonce,
      used: 0,
      used_encrypted: usedEncrypted.encrypted,
      used_nonce: usedEncrypted.nonce,
      due_date: card.dueDate,
      closing_date: card.closingDate,
      color: card.color,
      account_id: card.accountId,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('cards')
      .upsert(cardData);

    if (error) {
      console.error('[Supabase] Error saving card:', error);
      throw new Error(`Erro ao salvar cartão: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do cartão');
  }
};

export const loadCardsFromSupabase = async (): Promise<CreditCard[]> => {
  console.log('[Supabase] Loading cards for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('cards')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading cards:', error);
    throw new Error(`Erro ao carregar cartões: ${error.message}`);
  }

  const results: CreditCard[] = [];
  
  for (const row of data || []) {
    try {
      // Decrypt sensitive fields
      const name = await decryptField({
        encrypted: row.name_encrypted,
        nonce: row.name_nonce
      });
      
      const bank = await decryptField({
        encrypted: row.bank_encrypted,
        nonce: row.bank_nonce
      });
      
      const limit = await decryptField({
        encrypted: row.limit_encrypted,
        nonce: row.limit_nonce
      });
      
      const used = await decryptField({
        encrypted: row.used_encrypted,
        nonce: row.used_nonce
      });
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
        if (decrypted) return decrypted;
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(name, row.name),
        bank: cleanValue(bank, row.bank),
        limit: limit ? Number(limit) : Number(row.limit || 0),
        used: used ? Number(used) : Number(row.used || 0),
        dueDate: row.due_date,
        closingDate: row.closing_date,
        color: row.color,
        accountId: row.account_id,
        createdAt: row.created_at
      });
    } catch (decryptError) {
      console.warn(`[Supabase] ⚠️ Failed to decrypt card ${row.id}, using fallback values`);
      
      const cleanValue = (fallback: string | null | undefined): string => {
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(row.name) || 'Cartão',
        bank: cleanValue(row.bank),
        limit: Number(row.limit || 0),
        used: Number(row.used || 0),
        dueDate: row.due_date,
        closingDate: row.closing_date,
        color: row.color,
        accountId: row.account_id,
        createdAt: row.created_at
      });
    }
  }
  
  return results;
};

export const deleteCardFromSupabase = async (cardId: string): Promise<void> => {
  console.log('[Supabase] Deleting card:', cardId);

  const { error } = await supabase
    .from('cards')
    .delete()
    .eq('id', cardId);

  if (error) {
    console.error('[Supabase] Error deleting card:', error);
    throw new Error(`Erro ao excluir cartão: ${error.message}`);
  }
};

// ACCOUNTS
export const saveAccountToSupabase = async (account: BankAccount): Promise<void> => {
  console.log('[Supabase] Saving account with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(account.name);
    const bankEncrypted = await encryptField(account.bank || '');
    const balanceEncrypted = await encryptField(String(account.balance));
    const initialBalanceEncrypted = await encryptField(String(account.balance));
    const accountNumberEncrypted = await encryptField(account.accountNumber || '');
    
    const accountData = addProfileId({
      id: account.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      bank: '[ENCRYPTED]',
      bank_encrypted: bankEncrypted.encrypted,
      bank_nonce: bankEncrypted.nonce,
      type: account.accountType, // Use accountType for the type field to match constraint
      account_type: account.accountType,
      balance: 0,
      balance_encrypted: balanceEncrypted.encrypted,
      balance_nonce: balanceEncrypted.nonce,
      initial_balance: 0,
      initial_balance_encrypted: initialBalanceEncrypted.encrypted,
      initial_balance_nonce: initialBalanceEncrypted.nonce,
      account_number: '[ENCRYPTED]',
      account_number_encrypted: accountNumberEncrypted.encrypted,
      account_number_nonce: accountNumberEncrypted.nonce,
      color: account.color,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('accounts')
      .upsert(accountData);

    if (error) {
      console.error('[Supabase] Error saving account:', error);
      throw new Error(`Erro ao salvar conta: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da conta');
  }
};

export const loadAccountsFromSupabase = async (): Promise<BankAccount[]> => {
  console.log('[Supabase] Loading accounts for profile:', getCurrentProfileId());
  
  // Verify encryption system is ready
  const { isEncryptionHealthy } = await import('./encryption-health-check');
  if (!isEncryptionHealthy()) {
    console.warn('[Supabase] Encryption not healthy, aborting account load');
    throw new Error('Sistema de criptografia não está pronto');
  }
  
  let query = supabase
    .from('accounts')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading accounts:', error);
    throw new Error(`Erro ao carregar contas: ${error.message}`);
  }

  const results: BankAccount[] = [];
  
  for (const row of data || []) {
    try {
      // Decrypt sensitive fields
      const name = await decryptField({
        encrypted: row.name_encrypted,
        nonce: row.name_nonce
      });
      
      const bank = await decryptField({
        encrypted: row.bank_encrypted,
        nonce: row.bank_nonce
      });
      
      const balance = await decryptField({
        encrypted: row.balance_encrypted,
        nonce: row.balance_nonce
      });
      
      const accountNumber = await decryptField({
        encrypted: row.account_number_encrypted,
        nonce: row.account_number_nonce
      });
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
        if (decrypted) return decrypted;
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(name, row.name),
        bank: cleanValue(bank, row.bank),
        accountType: row.account_type as 'corrente' | 'poupanca' | 'investimento',
        balance: balance ? Number(balance) : Number(row.balance || 0),
        accountNumber: cleanValue(accountNumber, row.account_number),
        color: row.color
      });
    } catch (decryptError) {
      console.warn(`[Supabase] ⚠️ Failed to decrypt account ${row.id}:`, decryptError);
      console.warn(`[Supabase] ⚠️ This may indicate encryption system not ready or corrupted data`);
      
      const cleanValue = (fallback: string | null | undefined): string => {
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(row.name) || 'Conta',
        bank: cleanValue(row.bank),
        accountType: row.account_type as 'corrente' | 'poupanca' | 'investimento',
        balance: Number(row.balance || 0),
        accountNumber: cleanValue(row.account_number),
        color: row.color
      });
    }
  }
  
  return results;
};

export const deleteAccountFromSupabase = async (accountId: string): Promise<void> => {
  console.log('[Supabase] Deleting account:', accountId);

  const { error } = await supabase
    .from('accounts')
    .delete()
    .eq('id', accountId);

  if (error) {
    console.error('[Supabase] Error deleting account:', error);
    throw new Error(`Erro ao excluir conta: ${error.message}`);
  }
};

// BUDGETS
export const saveBudgetToSupabase = async (budget: Budget): Promise<void> => {
  console.log('[Supabase] Saving budget with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(budget.name);
    const amountEncrypted = await encryptField(String(budget.amount));
    const spentEncrypted = await encryptField(String(budget.spent || 0));
    
    const budgetData = addProfileId({
      id: budget.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      category_id: budget.categoryId,
      amount: 0,
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      spent: 0,
      spent_encrypted: spentEncrypted.encrypted,
      spent_nonce: spentEncrypted.nonce,
      period: budget.period || 'mensal',
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('budgets')
      .upsert(budgetData);

    if (error) {
      console.error('[Supabase] Error saving budget:', error);
      throw new Error(`Erro ao salvar orçamento: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do orçamento');
  }
};

export const loadBudgetsFromSupabase = async (): Promise<Budget[]> => {
  console.log('[Supabase] Loading budgets for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('budgets')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading budgets:', error);
    throw new Error(`Erro ao carregar orçamentos: ${error.message}`);
  }

  const results: Budget[] = [];
  
  for (const row of data || []) {
    try {
      // Decrypt sensitive fields
      const name = await decryptField({
        encrypted: row.name_encrypted,
        nonce: row.name_nonce
      });
      
      const amount = await decryptField({
        encrypted: row.amount_encrypted,
        nonce: row.amount_nonce
      });
      
      const spent = await decryptField({
        encrypted: row.spent_encrypted,
        nonce: row.spent_nonce
      });
      
      // Helper function to clean encrypted placeholders
      const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
        if (decrypted) return decrypted;
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(name, row.name),
        categoryId: row.category_id,
        amount: amount ? Number(amount) : Number(row.amount || 0),
        spent: spent ? Number(spent) : Number(row.spent || 0),
        period: row.period as 'diario' | 'semanal' | 'mensal' | 'anual' || 'mensal',
        createdAt: row.created_at
      });
    } catch (decryptError) {
      console.warn(`[Supabase] ⚠️ Failed to decrypt budget ${row.id}, using fallback values`);
      
      const cleanValue = (fallback: string | null | undefined): string => {
        if (!fallback || fallback === '[ENCRYPTED]') return '';
        return fallback;
      };
      
      results.push({
        id: row.id,
        name: cleanValue(row.name) || 'Orçamento',
        categoryId: row.category_id,
        amount: Number(row.amount || 0),
        spent: Number(row.spent || 0),
        period: row.period as 'diario' | 'semanal' | 'mensal' | 'anual' || 'mensal',
        createdAt: row.created_at
      });
    }
  }
  
  return results;
};

export const deleteBudgetFromSupabase = async (budgetId: string): Promise<void> => {
  console.log('[Supabase] Deleting budget:', budgetId);

  const { error } = await supabase
    .from('budgets')
    .delete()
    .eq('id', budgetId);

  if (error) {
    console.error('[Supabase] Error deleting budget:', error);
    throw new Error(`Erro ao excluir orçamento: ${error.message}`);
  }
};

// GOALS
export const saveGoalToSupabase = async (goal: Goal): Promise<void> => {
  console.log('[Supabase] Saving goal with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(goal.name);
    const targetAmountEncrypted = await encryptField(String(goal.targetValue));
    const currentAmountEncrypted = await encryptField(String(goal.currentValue || goal.saved));
    
    const goalData = addProfileId({
      id: goal.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      target_amount: 0,
      target_amount_encrypted: targetAmountEncrypted.encrypted,
      target_amount_nonce: targetAmountEncrypted.nonce,
      current_amount: 0,
      current_amount_encrypted: currentAmountEncrypted.encrypted,
      current_amount_nonce: currentAmountEncrypted.nonce,
      target_date: goal.deadline,
      category: goal.category,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('goals')
      .upsert(goalData);

    if (error) {
      console.error('[Supabase] Error saving goal:', error);
      throw new Error(`Erro ao salvar meta: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da meta');
  }
};

export const loadGoalsFromSupabase = async (): Promise<Goal[]> => {
  console.log('[Supabase] Loading goals for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('goals')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading goals:', error);
    throw new Error(`Erro ao carregar metas: ${error.message}`);
  }

  return (data || []).map(row => ({
    id: row.id,
    name: row.name,
    amount: Number(row.target_amount || row.target_value || 0),
    currentValue: Number(row.current_amount || row.current_value || row.saved || 0),
    targetValue: Number(row.target_amount || row.target_value || 0),
    deadline: row.target_date || row.deadline,
    category: row.category || '',
    createdAt: row.created_at,
    saved: Number(row.current_amount || row.current_value || row.saved || 0)
  }));
};

export const deleteGoalFromSupabase = async (goalId: string): Promise<void> => {
  console.log('[Supabase] Deleting goal:', goalId);

  const { error } = await supabase
    .from('goals')
    .delete()
    .eq('id', goalId);

  if (error) {
    console.error('[Supabase] Error deleting goal:', error);
    throw new Error(`Erro ao excluir meta: ${error.message}`);
  }
};

export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  return !!user;
};
